import { Injectable } from '@angular/core';

/**
 * Tracks whether any screen currently holds unsaved user data.
 *
 * Components with editable, not-yet-persisted state register themselves while
 * dirty and clear themselves once saved or destroyed:
 *
 * ```ts
 * private readonly unsaved = inject(UnsavedChangesService);
 * // when a form becomes dirty:
 * this.unsaved.markDirty(this);
 * // after save / on reset / in ngOnDestroy:
 * this.unsaved.markPristine(this);
 * ```
 *
 * Consumed by {@link VersionCheckService} (to warn before a soft-update reload)
 * and by the global `beforeunload` guard it installs.
 */
@Injectable({ providedIn: 'root' })
export class UnsavedChangesService {
  /** Distinct sources (usually component instances) that currently hold unsaved data. */
  private readonly sources = new Set<object>();

  /** Flag `source` as holding unsaved changes. Idempotent. */
  markDirty(source: object): void {
    this.sources.add(source);
  }

  /** Clear `source`'s unsaved-changes flag (after save, reset, or destroy). Idempotent. */
  markPristine(source: object): void {
    this.sources.delete(source);
  }

  /** True when at least one source still holds unsaved data. */
  hasUnsavedChanges(): boolean {
    return this.sources.size > 0;
  }

  /** Drop all tracked sources (e.g. right before an intentional reload). */
  clear(): void {
    this.sources.clear();
  }
}
